import { useEffect, useMemo, useState, useRef } from "react";
import SpellcheckTextarea from "@/components/SpellcheckTextarea";
import api, { formatApiError } from "@/lib/api";
import { formatLicenseDateForDisplay } from "@/lib/licenseDates";
import { PageHeader } from "./layoutChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { useWorkspaceMode } from "@/lib/workspaceMode";
import { useAuth } from "@/lib/auth";
import {
  Loader2,
  Pencil,
  Plus,
  Trash2,
  AlertTriangle,
  RefreshCw,
  FileDown,
  Upload,
} from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import RecuperacionDistribuidorPanel from "@/components/RecuperacionDistribuidorPanel";
import { CONFIGURACION_EMISOR_PATH } from "@/modules/config/configuracionRoutes";

function daysUntil(isoDate) {
  if (!isoDate) return null;
  try {
    const end = new Date(isoDate.slice(0, 10));
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    return Math.round((end - t) / (86400 * 1000));
  } catch {
    return null;
  }
}

const PAY_OPTIONS = [
  { value: "pendiente", label: "Pendiente" },
  { value: "pagado", label: "Pagado" },
  { value: "parcial", label: "Parcial" },
  { value: "cortesia", label: "Cortesía" },
  { value: "otro", label: "Otro" },
];

export default function CarteraLicencias() {
  const { user } = useAuth();
  const { mode, canSwitch } = useWorkspaceMode();
  const [items, setItems] = useState([]);
  const [expSoon, setExpSoon] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter30Days, setFilter30Days] = useState(false);
  const [search, setSearch] = useState("");
  const [payFilter, setPayFilter] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [creating, setCreating] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manual, setManual] = useState({
    licensee_name: "",
    licensee_rfc: "",
    licensee_email: "",
    installation_id: "",
    valid_from: new Date().toISOString().slice(0, 10),
    valid_to: "",
    plan: "standard",
    payment_status: "pendiente",
    payment_amount: "",
    payment_date: "",
    payment_method: "",
    activation_status: "pending",
    notes: "",
    portal_username: "",
    portal_password: "",
    contract_equipment_total: "",
    contract_equipment_slot: "",
  });
  const [buyersMap, setBuyersMap] = useState({});
  const [groupedView, setGroupedView] = useState(true);
  const [buyerEditOpen, setBuyerEditOpen] = useState(false);
  const [buyerEdit, setBuyerEdit] = useState(null);

  const solicitudFileRef = useRef(null);
  const [solicitudTargetId, setSolicitudTargetId] = useState(null);
  const [busySolicitudUpload, setBusySolicitudUpload] = useState(false);

  const [searchParams] = useSearchParams();
  const buyerIdFilter = searchParams.get("buyer_id") || "";

  const canUse = Boolean(user?.can_generate_licenses);
  const wrongMode = canSwitch && mode !== "distribuidor";

  const load = async () => {
    if (!canUse) return;
    setLoading(true);
    try {
      const params = {};
      if (filter30Days) params.expiring_within_days = 30;
      if (payFilter) params.payment_status = payFilter;
      if (search.trim()) params.q = search.trim();
      if (buyerIdFilter) params.buyer_id = buyerIdFilter;
      const { data } = await api.get("/distributor/license-registry", { params });
      setItems(data.items || []);
      setBuyersMap(data.buyers || {});
      setExpSoon(data.expiring_soon_count ?? 0);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al cargar cartera");
      setItems([]);
      setBuyersMap({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canUse && !wrongMode) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canUse, wrongMode, filter30Days, payFilter, buyerIdFilter]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    load();
  };

  const openEdit = (row) => {
    setEditRow({
      ...row,
      payment_amount: row.payment_amount != null ? String(row.payment_amount) : "",
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editRow?.id) return;
    try {
      await api.put(`/distributor/license-registry/${editRow.id}`, {
        payment_status: editRow.payment_status,
        payment_amount: editRow.payment_amount === "" ? null : Number(editRow.payment_amount),
        payment_date: editRow.payment_date || "",
        payment_method: editRow.payment_method || "",
        activation_status: editRow.activation_status,
        notes: editRow.notes || "",
        licensee_email: editRow.licensee_email || "",
      });
      toast.success("Registro actualizado");
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al guardar");
    }
  };

  const removeRow = async (id) => {
    if (!window.confirm("¿Eliminar este registro de la cartera?")) return;
    try {
      await api.delete(`/distributor/license-registry/${id}`);
      toast.success("Eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error");
    }
  };

  const pickSolicitudUpload = (entryId) => {
    setSolicitudTargetId(entryId);
    solicitudFileRef.current?.click();
  };

  const onSolicitudFilePicked = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    const tid = solicitudTargetId;
    setSolicitudTargetId(null);
    if (!f || !tid) return;
    setBusySolicitudUpload(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.put(`/distributor/license-registry/${tid}/solicitud-pdf`, fd);
      toast.success("Solicitud de aceptación guardada (PDF)");
      if (editOpen && editRow?.id === tid) {
        setEditRow((prev) => (prev ? { ...prev, ...data } : prev));
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo subir el PDF");
    } finally {
      setBusySolicitudUpload(false);
    }
  };

  const downloadRegistrySolicitud = async (entryId) => {
    try {
      const { data } = await api.get(`/distributor/license-registry/${entryId}/solicitud-pdf`, {
        responseType: "blob",
      });
      if (!(data instanceof Blob) || data.size < 4) {
        toast.error("Respuesta inválida al descargar");
        return;
      }
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `solicitud-aceptacion-${entryId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo descargar el PDF");
    }
  };

  const licenseGroups = useMemo(() => {
    const m = new Map();
    for (const row of items) {
      const bid = row.buyer_id || `__fb__${row.licensee_rfc}|${row.installation_id}`;
      if (!m.has(bid)) {
        const meta = row.buyer_id ? buyersMap[row.buyer_id] : null;
        m.set(bid, {
          buyerId: row.buyer_id || null,
          labelName: meta?.licensee_name || row.licensee_name,
          labelRfc: meta?.licensee_rfc || row.licensee_rfc,
          email: meta?.licensee_email || row.licensee_email || "",
          licenses: [],
        });
      }
      m.get(bid).licenses.push(row);
    }
    return Array.from(m.entries()).sort((a, b) =>
      (a[1].labelName || "").localeCompare(b[1].labelName || "", "es", { sensitivity: "base" })
    );
  }, [items, buyersMap]);

  const openBuyerEdit = (buyerId) => {
    const b = buyersMap[buyerId];
    if (!b) {
      toast.error("No hay ficha de comprador");
      return;
    }
    setBuyerEdit({ ...b });
    setBuyerEditOpen(true);
  };

  const saveBuyer = async () => {
    if (!buyerEdit?.id) return;
    try {
      await api.put(`/distributor/buyers/${buyerEdit.id}`, {
        licensee_name: buyerEdit.licensee_name,
        licensee_email: buyerEdit.licensee_email,
        notes: buyerEdit.notes || "",
        equipos_contratados:
          buyerEdit.equipos_contratados === "" || buyerEdit.equipos_contratados == null
            ? undefined
            : Number(buyerEdit.equipos_contratados),
      });
      toast.success("Comprador actualizado");
      setBuyerEditOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error");
    }
  };

  const saveManual = async () => {
    if (!manual.licensee_name || !manual.licensee_rfc || !manual.installation_id || !manual.valid_to) {
      toast.error("Completa nombre, RFC, ID instalación y vigencia hasta");
      return;
    }
    const ctRaw = String(manual.contract_equipment_total ?? "").trim();
    const csRaw = String(manual.contract_equipment_slot ?? "").trim();
    if (csRaw && !ctRaw) {
      toast.error("Indica equipos del contrato si pones número de equipo");
      return;
    }
    if (ctRaw && csRaw) {
      const ct = parseInt(ctRaw, 10);
      const cs = parseInt(csRaw, 10);
      if (!Number.isFinite(ct) || !Number.isFinite(cs) || cs > ct) {
        toast.error("Número de equipo inválido o mayor que el total");
        return;
      }
    }
    const u = (manual.portal_username || "").trim().toLowerCase();
    const pw = (manual.portal_password || "").trim();
    if (u && pw.length > 0 && pw.length < 6) {
      toast.error("La contraseña del portal debe tener al menos 6 caracteres");
      return;
    }
    setCreating(true);
    try {
      const ct0 = String(manual.contract_equipment_total ?? "").trim();
      const cs0 = String(manual.contract_equipment_slot ?? "").trim();
      const {
        contract_equipment_total: _x,
        contract_equipment_slot: _y,
        ...manualRest
      } = manual;
      const payload = {
        ...manualRest,
        portal_username: u,
        portal_password: pw.length >= 6 ? pw : undefined,
        payment_amount:
          manual.payment_amount === "" ? null : Number(manual.payment_amount),
      };
      if (ct0) payload.contract_equipment_total = parseInt(ct0, 10);
      if (cs0) payload.contract_equipment_slot = parseInt(cs0, 10);
      await api.post("/distributor/license-registry", payload);
      toast.success("Registro creado");
      setManualOpen(false);
      setManual({
        licensee_name: "",
        licensee_rfc: "",
        licensee_email: "",
        installation_id: "",
        valid_from: new Date().toISOString().slice(0, 10),
        valid_to: "",
        plan: "standard",
        payment_status: "pendiente",
        payment_amount: "",
        payment_date: "",
        payment_method: "",
        activation_status: "pending",
        notes: "",
        portal_username: "",
        portal_password: "",
        contract_equipment_total: "",
        contract_equipment_slot: "",
      });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error");
    } finally {
      setCreating(false);
    }
  };

  function contractEquipLabel(row) {
    const t = row.contract_equipment_total;
    const s = row.contract_equipment_slot;
    if (s != null && t != null) return `${s} / ${t}`;
    if (t != null) return `— / ${t}`;
    return "—";
  }

  function renderLicenseTableBody(rows) {
    return rows.map((row) => {
      const d = daysUntil(row.valid_to);
      let dayClass = "text-slate-700";
      if (d != null && d >= 0 && d <= 7) dayClass = "text-red-600 font-semibold";
      else if (d != null && d >= 0 && d <= 30) dayClass = "text-amber-700 font-medium";
      else if (d != null && d < 0) dayClass = "text-slate-400 line-through";
      return (
        <TableRow key={row.id}>
          <TableCell className="font-medium max-w-[10rem] truncate" title={row.licensee_name}>
            {row.licensee_name}
          </TableCell>
          <TableCell className="font-mono text-xs">{row.licensee_rfc}</TableCell>
          <TableCell className="font-mono text-xs">{row.installation_id}</TableCell>
          <TableCell className="text-[11px] font-mono text-slate-700 whitespace-nowrap" title="Equipo / total contrato">
            {contractEquipLabel(row)}
          </TableCell>
          <TableCell className="text-xs capitalize">{row.plan}</TableCell>
          <TableCell>
            <div className="text-xs">{formatLicenseDateForDisplay(row.valid_to)}</div>
            <div className="text-[10px] text-slate-500 font-mono" title="ISO">
              {row.valid_to ? String(row.valid_to).slice(0, 10) : "—"}
            </div>
          </TableCell>
          <TableCell className={`text-xs num ${dayClass}`}>
            {d == null ? "—" : d < 0 ? `+${-d} venc.` : d}
          </TableCell>
          <TableCell className="text-xs capitalize">{row.payment_status}</TableCell>
          <TableCell className="text-xs capitalize">
            {row.activation_status === "active" ? "Activa" : "Pendiente"}
          </TableCell>
          <TableCell className="text-center whitespace-nowrap">
            {row.has_solicitud_pdf ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                title="Descargar solicitud de aceptación (PDF)"
                onClick={() => downloadRegistrySolicitud(row.id)}
              >
                <FileDown className="w-3.5 h-3.5 text-emerald-700" />
              </Button>
            ) : (
              <span className="text-[10px] text-slate-400">—</span>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              title="Adjuntar o reemplazar solicitud de aceptación (PDF)"
              disabled={busySolicitudUpload}
              onClick={() => pickSolicitudUpload(row.id)}
            >
              <Upload className="w-3.5 h-3.5 text-slate-600" />
            </Button>
          </TableCell>
          <TableCell className="text-right whitespace-nowrap">
            <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => openEdit(row)}>
              <Pencil className="w-3.5 h-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-rose-600"
              onClick={() => removeRow(row.id)}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </TableCell>
        </TableRow>
      );
    });
  }

  const bannerStyle = useMemo(() => {
    if (!canUse)
      return {
        kind: "no",
        text: "Esta instalación está compilada como cliente final: la cartera de licencias no está disponible (ALLOW_LICENSE_GENERATION=false).",
      };
    if (wrongMode)
      return {
        kind: "mode",
        text: "Para ver y editar la cartera, elige «Distribuidor (vender sistema)» en Configuración → Sistema.",
      };
    return null;
  }, [canUse, wrongMode]);

  if (bannerStyle) {
    return (
      <div>
        <PageHeader
          title="Compradores (licencias)"
          subtitle="Quienes compraron el sistema: varias licencias por RFC. Distinto del menú «Clientes» (cartera fiscal CFDI)."
        />
        <div className="p-8">
          <div
            className={`rounded-sm border px-4 py-3 text-sm ${
              bannerStyle.kind === "no"
                ? "border-slate-300 bg-slate-50 text-slate-800"
                : "border-amber-300 bg-amber-50 text-amber-950"
            }`}
          >
            {bannerStyle.kind === "mode" && (
              <span className="font-semibold">Modo Despacho activo. </span>
            )}
            {bannerStyle.text}
          </div>
          {bannerStyle.kind === "mode" && (
            <p className="text-xs text-slate-500 mt-4">
              También puedes ir a{" "}
              <Link to={CONFIGURACION_EMISOR_PATH} className="underline font-medium text-[#0F2D5C]">
                Gestor de licencias
              </Link>{" "}
              en modo Distribuidor para emitir tokens.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Licencias emitidas"
        subtitle={
          buyerIdFilter
            ? "Filtrando por un comprador del catálogo. El maestro de datos comerciales está en «Catálogo de compradores»."
            : "Agrupado por comprador (RFC). Vigencias, cobros y activaciones por licencia. Los clientes fiscales del despacho siguen en «Clientes»."
        }
        right={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" className="h-9" onClick={() => load()}>
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Actualizar
            </Button>
            <Button size="sm" className="h-9 bg-violet-700 hover:bg-violet-800" onClick={() => setManualOpen(true)}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Alta manual
            </Button>
          </div>
        }
      />

      <input
        ref={solicitudFileRef}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        onChange={onSolicitudFilePicked}
      />

      <div className="p-8 space-y-4">
        <RecuperacionDistribuidorPanel />
        {buyerIdFilter ? (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-sm border border-violet-200 bg-violet-50/80 px-4 py-2 text-xs text-slate-800">
            <span>
              Mostrando solo licencias del comprador
              {buyersMap[buyerIdFilter]?.licensee_name ? (
                <>
                  : <strong>{buyersMap[buyerIdFilter].licensee_name}</strong> ({buyersMap[buyerIdFilter]?.licensee_rfc || buyerIdFilter.slice(0, 8)})
                </>
              ) : (
                <> (ID {buyerIdFilter.slice(0, 8)}…)</>
              )}
              .
            </span>
            <Button variant="outline" size="sm" className="h-8" asChild>
              <Link to="/cartera-licencias">Quitar filtro</Link>
            </Button>
          </div>
        ) : null}
        {expSoon > 0 && (
          <div className="flex items-start gap-2 rounded-sm border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <span className="font-semibold">{expSoon} licencia(s)</span> con vigencia que termina en los próximos{" "}
              30 días (según fechas <code className="text-xs bg-white/80 px-1 rounded">valid_to</code> registradas).
            </div>
          </div>
        )}

        <form
          onSubmit={onSearchSubmit}
          className="flex flex-wrap items-end gap-3 bg-white border border-slate-200 rounded-sm p-4"
        >
          <div className="flex-1 min-w-[12rem]">
            <Label className="text-xs">Buscar</Label>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Nombre, RFC, correo, ID instalación…"
              className="h-9 mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">Pago</Label>
            <Select value={payFilter || "__all__"} onValueChange={(v) => setPayFilter(v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-9 w-40 mt-1">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos</SelectItem>
                {PAY_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 pb-1">
            <input
              id="grp"
              type="checkbox"
              checked={groupedView}
              onChange={(e) => setGroupedView(e.target.checked)}
              className="rounded border-slate-300"
            />
            <label htmlFor="grp" className="text-xs cursor-pointer">
              Vista agrupada por comprador
            </label>
          </div>
          <div className="flex items-center gap-2 pb-1">
            <input
              id="f30"
              type="checkbox"
              checked={filter30Days}
              onChange={(e) => setFilter30Days(e.target.checked)}
              className="rounded border-slate-300"
            />
            <label htmlFor="f30" className="text-xs cursor-pointer">
              Solo vencen en 30 días
            </label>
          </div>
          <Button type="submit" variant="secondary" className="h-9">
            Aplicar filtros
          </Button>
        </form>

        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          {loading ? (
            <div className="p-12 flex justify-center text-slate-500">
              <Loader2 className="w-7 h-7 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-600">
              No hay registros con estos criterios. Emite una licencia desde{" "}
              <Link to={CONFIGURACION_EMISOR_PATH} className="underline text-[#0F2D5C] font-medium">
                Gestor de licencias
              </Link>{" "}
              (con «Guardar en cartera») o usa «Alta manual».
            </div>
          ) : groupedView ? (
            <div className="p-3 space-y-3">
              {licenseGroups.map(([gk, g]) => (
                <details key={gk} className="border border-slate-200 rounded-sm bg-white" open>
                  <summary className="px-4 py-3 cursor-pointer list-none flex flex-wrap items-center gap-2 bg-slate-50 border-b border-slate-100 [&::-webkit-details-marker]:hidden">
                    <span className="font-semibold text-[#0F2D5C]">{g.labelName}</span>
                    <span className="font-mono text-xs text-slate-600">{g.labelRfc}</span>
                    {g.email ? (
                      <span className="text-xs text-slate-500 truncate max-w-[14rem]" title={g.email}>
                        {g.email}
                      </span>
                    ) : null}
                    <span className="text-xs rounded-full bg-violet-100 text-violet-800 px-2 py-0.5">
                      {g.licenses.length} lic.
                    </span>
                    {g.buyerId && buyersMap[g.buyerId]?.equipos_contratados ? (
                      <span className="text-[10px] rounded-full bg-sky-100 text-sky-900 px-2 py-0.5 font-medium">
                        Meta contrato: {buyersMap[g.buyerId].equipos_contratados} equipos
                      </span>
                    ) : null}
                    {g.buyerId ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 text-[11px] ml-auto"
                        onClick={(e) => {
                          e.preventDefault();
                          openBuyerEdit(g.buyerId);
                        }}
                      >
                        Ficha comprador
                      </Button>
                    ) : null}
                  </summary>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="whitespace-nowrap">Titular registro</TableHead>
                        <TableHead>RFC</TableHead>
                        <TableHead>ID inst.</TableHead>
                        <TableHead className="whitespace-nowrap">Equipo / total</TableHead>
                        <TableHead>Plan</TableHead>
                        <TableHead>Hasta</TableHead>
                        <TableHead>Días</TableHead>
                        <TableHead>Pago</TableHead>
                        <TableHead>Activación</TableHead>
                        <TableHead
                          className="whitespace-nowrap text-center w-[5.5rem] text-[11px] leading-tight px-1"
                          title="Solicitud de aceptación: PDF con declaración (se genera al emitir; puede reemplazarse)."
                        >
                          Solic.
                          <br />
                          acept.
                        </TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>{renderLicenseTableBody(g.licenses)}</TableBody>
                  </Table>
                </details>
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="whitespace-nowrap">Titular registro</TableHead>
                  <TableHead>RFC</TableHead>
                  <TableHead>ID inst.</TableHead>
                  <TableHead className="whitespace-nowrap">Equipo / total</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Hasta</TableHead>
                  <TableHead>Días</TableHead>
                  <TableHead>Pago</TableHead>
                  <TableHead>Activación</TableHead>
                  <TableHead
                    className="whitespace-nowrap text-center w-[5.5rem] text-[11px] leading-tight px-1"
                    title="Solicitud de aceptación: PDF con declaración (se genera al emitir; puede reemplazarse)."
                  >
                    Solic.
                    <br />
                    acept.
                  </TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>{renderLicenseTableBody(items)}</TableBody>
            </Table>
          )}
        </div>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar registro</DialogTitle>
          </DialogHeader>
          {editRow && (
            <div className="space-y-3 text-sm">
              <div className="text-xs text-slate-500">
                {editRow.licensee_name} · {editRow.licensee_rfc}
              </div>
              <div>
                <Label className="text-xs">Vigencia hasta (según licencia emitida)</Label>
                <div className="mt-1 rounded-sm border border-slate-200 bg-slate-50 px-3 py-2 space-y-0.5">
                  <div className="text-sm text-slate-900">
                    {editRow.valid_to ? formatLicenseDateForDisplay(editRow.valid_to) : "—"}
                  </div>
                  <div className="text-[10px] text-slate-500 font-mono select-all">
                    {editRow.valid_to?.slice(0, 10) || "—"}
                  </div>
                </div>
                <p className="text-[10px] text-slate-500 mt-1 leading-snug">
                  Fecha del token guardada al generar el registro; no se puede cambiar aquí para evitar discrepancias con la clave enviada. Para otra vigencia, emite una licencia nueva.
                </p>
              </div>
              <div>
                <Label className="text-xs">Correo licenciatario</Label>
                <Input
                  value={editRow.licensee_email || ""}
                  onChange={(e) => setEditRow({ ...editRow, licensee_email: e.target.value })}
                  className="h-9 mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Estado de pago</Label>
                <Select
                  value={editRow.payment_status}
                  onValueChange={(v) => setEditRow({ ...editRow, payment_status: v })}
                >
                  <SelectTrigger className="h-9 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAY_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Monto</Label>
                  <Input
                    value={editRow.payment_amount}
                    onChange={(e) => setEditRow({ ...editRow, payment_amount: e.target.value })}
                    className="h-9 mt-1"
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label className="text-xs">Fecha pago</Label>
                  <Input
                    type="date"
                    value={editRow.payment_date?.slice(0, 10) || ""}
                    onChange={(e) => setEditRow({ ...editRow, payment_date: e.target.value })}
                    className="h-9 mt-1"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Forma de pago</Label>
                <Input
                  value={editRow.payment_method || ""}
                  onChange={(e) => setEditRow({ ...editRow, payment_method: e.target.value })}
                  className="h-9 mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Activación en equipo cliente</Label>
                <Select
                  value={editRow.activation_status}
                  onValueChange={(v) => setEditRow({ ...editRow, activation_status: v })}
                >
                  <SelectTrigger className="h-9 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pendiente</SelectItem>
                    <SelectItem value="active">Activa (confirmada)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Notas</Label>
                <SpellcheckTextarea
                  value={editRow.notes || ""}
                  onChange={(e) => setEditRow({ ...editRow, notes: e.target.value })}
                  rows={3}
                  className="w-full mt-1 rounded-sm border border-slate-300 px-2 py-1.5 text-xs"
                />
              </div>
              <div className="rounded-sm border border-slate-200 bg-slate-50/80 p-3 space-y-2">
                <Label className="text-xs font-semibold text-slate-800">
                  Solicitud de aceptación (PDF; se genera al emitir, se puede reemplazar)
                </Label>
                {editRow.has_solicitud_pdf ? (
                  <p className="text-[10px] text-emerald-800">
                    Hay un archivo guardado en cartera. Puede descargarlo o subir uno nuevo para reemplazarlo.
                  </p>
                ) : (
                  <p className="text-[10px] text-slate-600">Aún no hay PDF adjunto para esta licencia.</p>
                )}
                <div className="flex flex-wrap gap-2">
                  {editRow.has_solicitud_pdf ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs"
                      onClick={() => downloadRegistrySolicitud(editRow.id)}
                    >
                      <FileDown className="w-3.5 h-3.5 mr-1" /> Descargar PDF
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                    disabled={busySolicitudUpload}
                    onClick={() => pickSolicitudUpload(editRow.id)}
                  >
                    <Upload className="w-3.5 h-3.5 mr-1" />
                    {editRow.has_solicitud_pdf ? "Reemplazar solicitud" : "Adjuntar solicitud"}
                  </Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              Cerrar
            </Button>
            <Button onClick={saveEdit} className="bg-[#0F2D5C]">
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Alta manual en cartera</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="sm:col-span-2">
              <Label className="text-xs">Razón social / nombre</Label>
              <Input
                value={manual.licensee_name}
                onChange={(e) => setManual({ ...manual, licensee_name: e.target.value })}
                className="h-9 mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">RFC</Label>
              <Input
                value={manual.licensee_rfc}
                onChange={(e) => setManual({ ...manual, licensee_rfc: e.target.value.toUpperCase() })}
                className="h-9 mt-1 font-mono"
              />
            </div>
            <div>
              <Label className="text-xs">Correo</Label>
              <Input
                value={manual.licensee_email}
                onChange={(e) => setManual({ ...manual, licensee_email: e.target.value })}
                className="h-9 mt-1"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">ID instalación (VF-…)</Label>
              <Input
                value={manual.installation_id}
                onChange={(e) => setManual({ ...manual, installation_id: e.target.value })}
                className="h-9 mt-1 font-mono"
              />
            </div>
            <div>
              <Label className="text-xs">Vigencia desde</Label>
              <Input
                type="date"
                value={manual.valid_from}
                onChange={(e) => setManual({ ...manual, valid_from: e.target.value })}
                className="h-9 mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Vigencia hasta</Label>
              <Input
                type="date"
                value={manual.valid_to}
                onChange={(e) => setManual({ ...manual, valid_to: e.target.value })}
                className="h-9 mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Plan</Label>
              <Select value={manual.plan} onValueChange={(v) => setManual({ ...manual, plan: v })}>
                <SelectTrigger className="h-9 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Equipos del contrato (opcional)</Label>
              <Input
                type="number"
                min={1}
                max={99}
                value={manual.contract_equipment_total}
                onChange={(e) => setManual({ ...manual, contract_equipment_total: e.target.value })}
                className="h-9 mt-1 font-mono"
                placeholder="Total de PCs / licencias del mismo comprador"
              />
            </div>
            <div>
              <Label className="text-xs">Esta fila es el equipo N (opcional)</Label>
              <Input
                type="number"
                min={1}
                max={99}
                value={manual.contract_equipment_slot}
                onChange={(e) => setManual({ ...manual, contract_equipment_slot: e.target.value })}
                className="h-9 mt-1 font-mono"
                placeholder="1, 2, 3… (requiere total)"
              />
            </div>
            <div>
              <Label className="text-xs">Pago</Label>
              <Select
                value={manual.payment_status}
                onValueChange={(v) => setManual({ ...manual, payment_status: v })}
              >
                <SelectTrigger className="h-9 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Monto</Label>
              <Input
                value={manual.payment_amount}
                onChange={(e) => setManual({ ...manual, payment_amount: e.target.value })}
                className="h-9 mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Fecha pago</Label>
              <Input
                type="date"
                value={manual.payment_date}
                onChange={(e) => setManual({ ...manual, payment_date: e.target.value })}
                className="h-9 mt-1"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Forma de pago</Label>
              <Input
                value={manual.payment_method}
                onChange={(e) => setManual({ ...manual, payment_method: e.target.value })}
                className="h-9 mt-1"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Acceso web (opcional)</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
                <Input
                  value={manual.portal_username}
                  onChange={(e) =>
                    setManual({
                      ...manual,
                      portal_username: e.target.value.toLowerCase().replace(/\s+/g, ""),
                    })
                  }
                  className="h-9 font-mono text-xs"
                  placeholder="Usuario de acceso"
                  autoComplete="off"
                />
                <Input
                  type="password"
                  value={manual.portal_password}
                  onChange={(e) => setManual({ ...manual, portal_password: e.target.value })}
                  className="h-9 font-mono text-xs"
                  placeholder="Contraseña (mín. 6 si hay usuario)"
                  autoComplete="new-password"
                />
              </div>
              <p className="text-[10px] text-slate-500 mt-1">
                Sincroniza credenciales con el catálogo del comprador (válido usar usuario distinto al correo).
              </p>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Notas</Label>
              <SpellcheckTextarea
                value={manual.notes}
                onChange={(e) => setManual({ ...manual, notes: e.target.value })}
                rows={2}
                className="w-full mt-1 rounded-sm border px-2 py-1 text-xs"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={saveManual} disabled={creating} className="bg-violet-700">
              {creating && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin inline" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={buyerEditOpen} onOpenChange={setBuyerEditOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Ficha comprador (software)</DialogTitle>
          </DialogHeader>
          {buyerEdit && (
            <div className="space-y-3 text-sm">
              <p className="text-xs text-slate-500">
                Datos maestros por RFC. No sustituye a un cliente fiscal en la cartera CFDI.
              </p>
              <div className="font-mono text-xs text-slate-600">{buyerEdit.licensee_rfc}</div>
              <div>
                <Label className="text-xs">Nombre / razón social</Label>
                <Input
                  value={buyerEdit.licensee_name || ""}
                  onChange={(e) => setBuyerEdit({ ...buyerEdit, licensee_name: e.target.value })}
                  className="h-9 mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Correo</Label>
                <Input
                  value={buyerEdit.licensee_email || ""}
                  onChange={(e) => setBuyerEdit({ ...buyerEdit, licensee_email: e.target.value })}
                  className="h-9 mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Notas del comprador</Label>
                <SpellcheckTextarea
                  value={buyerEdit.notes || ""}
                  onChange={(e) => setBuyerEdit({ ...buyerEdit, notes: e.target.value })}
                  rows={4}
                  className="w-full mt-1 rounded-sm border border-slate-300 px-2 py-1.5 text-xs"
                  placeholder="Contacto comercial, facturación, recordatorios generales…"
                />
              </div>
              <div>
                <Label className="text-xs">Equipos contratados (meta)</Label>
                <Input
                  type="number"
                  min={1}
                  max={99}
                  value={buyerEdit.equipos_contratados != null ? String(buyerEdit.equipos_contratados) : ""}
                  onChange={(e) =>
                    setBuyerEdit({
                      ...buyerEdit,
                      equipos_contratados: e.target.value === "" ? "" : e.target.value,
                    })
                  }
                  className="h-9 mt-1 font-mono"
                  placeholder="Cuántas licencias/PCs contempla el contrato"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBuyerEditOpen(false)}>
              Cerrar
            </Button>
            <Button onClick={saveBuyer} className="bg-violet-700 hover:bg-violet-800">
              Guardar ficha
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
